import React, { useMemo } from 'react';
import { IRiskTolerance } from '../../../../sp-types/accountTypes';
import { AdditionalArrowsLinearGauge } from './LinearGaugeComponents/AdditionalArrowsLinearGauge';
import { ColoredSection } from './LinearGaugeComponents/ColoredSectionLinearGauge';
import { GaugeBarAreaTicks } from './LinearGaugeComponents/GaugeBarAreaTicks';
import { LinearGaugeCurrentCursor } from './LinearGaugeComponents/LinearGaugeCurrentCursor';
import {
  calculateAreaPercentage,
  formatAreaToToolTipText,
  generateColoredAreaData,
  handlePointerPosition,
} from './Utils';
import { GaugeBarTicks } from './LinearGaugeComponents/LinearGaugeBarTicks';

const DEFAULT_COLORS = ['#ff575e', '#ffbe5f', '#00af99'];

export interface LinearGaugeAdditionalArrowsDataType {
  img: string;
  value: number;
  bigArrow?: boolean;
}

export interface LinearGaugeProps {
  currentValue: number;
  riskTolerance: IRiskTolerance;
  hightInRem?: number;
  tickerHight?: number;
  isRounded?: boolean;
  colors?: [string, string, string];
  numberOfTicks?: 2 | 4 | 8 | 10 | 12;
  additionalArrows?: LinearGaugeAdditionalArrowsDataType[];
  isTicksByColors?: boolean;
  isZoomExposure?: boolean;
}

// The Component has hard coded hight - will need to fix eventually
export const LinearGauge: React.FC<LinearGaugeProps> = ({
  currentValue,
  riskTolerance,
  tickerHight = 0.75,
  hightInRem = 1.5,
  isRounded = false,
  colors = DEFAULT_COLORS,
  numberOfTicks = 4,
  additionalArrows = [],
  isTicksByColors = false,
  isZoomExposure = false,
}) => {
  const { dangerAreaMinus: propDangerMinus, dangerAreaPlus: propDangerPlus, stableArea } = riskTolerance;

  const dangerAreaMinus = isZoomExposure
    ? { ...propDangerMinus, start: Math.max(Math.abs(currentValue), propDangerPlus.start) * -2 }
    : propDangerMinus;

  const dangerAreaPlus = isZoomExposure
    ? { ...propDangerPlus, end: Math.max(Math.abs(currentValue), propDangerPlus.start) * 2 }
    : propDangerPlus;

  const calculations = useMemo(() => {
    const sumOfRange = dangerAreaPlus.end - dangerAreaMinus.start;
    const dangerLeftArea = calculateAreaPercentage(sumOfRange, dangerAreaMinus.start, dangerAreaMinus.end);
    const possibleLeftArea = calculateAreaPercentage(sumOfRange, dangerAreaMinus.end, stableArea.start);
    const optimalArea = calculateAreaPercentage(sumOfRange, stableArea.start, stableArea.end);
    const possibleRightArea = calculateAreaPercentage(sumOfRange, stableArea.end, dangerAreaPlus.start);
    const dangerRightArea = calculateAreaPercentage(sumOfRange, dangerAreaPlus.start, dangerAreaPlus.end);
    const currentPosition = handlePointerPosition(currentValue, sumOfRange, dangerAreaMinus.start);
    const zeroPosition = handlePointerPosition(0, sumOfRange, dangerAreaMinus.start);
    const arrowsPosition = additionalArrows.map((item) =>
      handlePointerPosition(item.value, sumOfRange, dangerAreaMinus.start),
    );

    return {
      dangerLeftArea,
      possibleLeftArea,
      optimalArea,
      possibleRightArea,
      dangerRightArea,
      currentPosition,
      arrowsPosition,
      zeroPosition,
    };
  }, [currentValue, dangerAreaMinus, dangerAreaPlus, stableArea, additionalArrows, isZoomExposure]);

  const toolTipTexts = useMemo(() => {
    const dangerMinText = formatAreaToToolTipText(dangerAreaMinus);
    const possibleMinText = formatAreaToToolTipText({ start: dangerAreaMinus.end, end: stableArea.start });
    const optimalText = formatAreaToToolTipText({ end: stableArea.end, start: stableArea.start });
    const possibleMaxText = formatAreaToToolTipText({ start: stableArea.end, end: dangerAreaPlus.start });
    const dangerMaxText = formatAreaToToolTipText({ start: dangerAreaPlus.start, end: dangerAreaPlus.end });

    return {
      dangerMinText,
      possibleMinText,
      optimalText,
      possibleMaxText,
      dangerMaxText,
    };
  }, [riskTolerance, isZoomExposure, dangerAreaMinus]);

  const isValidRange =
    dangerAreaMinus.start < dangerAreaMinus.end &&
    dangerAreaMinus.end < stableArea.start &&
    stableArea.start < stableArea.end &&
    stableArea.end < dangerAreaPlus.start &&
    dangerAreaPlus.start < dangerAreaPlus.end;

  // If the range is invalid, display an error message
  if (!isValidRange) {
    return (
      <div className="flex flex-col  w-full min-h-[1.5rem] bg-red opacity-40  relative text-white text-center  ">
        Please contact support
      </div>
    );
  }

  // Using here CSS inline because it ain't working properly with tailwind
  return (
    <div className=" relative flex flex-1 pt-[1.5rem]">
      <div className="flex flex-col grow">
        <div className="flex flex-col grow relative ">
          <div className="flex flex-col justify-end  ">
            <LinearGaugeCurrentCursor positionInPrec={calculations.currentPosition} />
            <div style={{ left: `${calculations.zeroPosition}%` }} className={`absolute h-full z-5 flex flex-col `}>
              <div className="h-full w-[0.1rem] bg-white "></div>
            </div>

            <AdditionalArrowsLinearGauge
              arrowPositionAsPercent={calculations.arrowsPosition}
              additionalArrowsData={additionalArrows}
            />
            <div style={{ minHeight: `${hightInRem}rem` }} className={` h-full flex flex-row `}>
              {generateColoredAreaData(colors, isRounded, toolTipTexts, calculations).map((item, index) => (
                <ColoredSection
                  color={item.color}
                  isRounded={item.isRounded}
                  toolTipText={item.toolTipText}
                  widthInPrec={item.widthInPrec}
                  isFirst={item.isFirst}
                  isLast={item.isLast}
                  key={index}
                />
              ))}
            </div>
          </div>
          {isTicksByColors ? (
            <GaugeBarTicks
              numberOfTicks={numberOfTicks}
              tickerHight={tickerHight}
              zeroPositionAsPrec={calculations.zeroPosition}
            />
          ) : (
            <GaugeBarAreaTicks
              riskToleranceValues={{ stableArea, dangerAreaMinus, dangerAreaPlus }}
              numberOfTicks={numberOfTicks}
              tickerHight={tickerHight}
              zeroPositionAsPrec={calculations.zeroPosition}
              calculations={calculations}
            />
          )}
        </div>
      </div>
    </div>
  );
};
